'use client';

import type { ReactNode } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { HeadingPage, MetaDefault } from '../../components/typography';
import { Tooltip } from '../../components/tooltip';
import {
  ObjectEditMeasureProvider,
  useObjectEditMeasureElementRef,
} from './objectEditMeasureContext';
import { ObjectEditProcessingOverlay } from './ObjectEditProcessingOverlay';
import { useCopilotProcessingWorkingLayerDismiss } from './useCopilotProcessingWorkingLayerDismiss';
import {
  OBJECT_EDIT_REVIEW_SCROLL_BOTTOM_CLEARANCE_PX,
  OBJECT_EDIT_TITLE_ICON_SLOT_HEIGHT,
  OBJECT_EDIT_TITLE_LINE_CLAMP,
} from './objectEditLayoutTokens';
import './copilot-progressing-state.css';

export const OBJECT_EDIT_CONTENT_MAX_WIDTH = '100%';

export type ObjectEditPageLayoutProps = {
  eyebrow: ReactNode;
  title: ReactNode;
  entityIcon?: ReactNode;
  actions: ReactNode;
  meta?: ReactNode;
  suggestionReviewBar?: ReactNode;
  children: ReactNode;
  isProcessing?: boolean;
  processingAriaLabel?: string;
  processingStatusText?: string;
  processingStopLabel?: string;
  onProcessingStop?: () => void;
};

function ObjectEditMeasureColumn({ children }: { children: ReactNode }) {
  const setMeasureElement = useObjectEditMeasureElementRef();

  return (
    <Box ref={setMeasureElement ?? undefined} w="full" minW={0}>
      {children}
    </Box>
  );
}

export function ObjectEditPageLayout({
  eyebrow,
  title,
  entityIcon,
  actions,
  meta,
  suggestionReviewBar,
  children,
  isProcessing,
  processingAriaLabel,
  processingStatusText,
  processingStopLabel,
  onProcessingStop,
}: ObjectEditPageLayoutProps) {
  const { scrollContainerRef, isWorkingLayerDismissed } =
    useCopilotProcessingWorkingLayerDismiss({
      isProcessing: Boolean(isProcessing),
    });
  const showWorkingLayer = Boolean(isProcessing) && !isWorkingLayerDismissed;
  const showReviewBar = Boolean(suggestionReviewBar) && !isProcessing;
  const shouldReserveReviewScrollClearance =
    showReviewBar || (Boolean(isProcessing) && isWorkingLayerDismissed);
  const showProcessingChrome =
    Boolean(isProcessing) &&
    Boolean(processingAriaLabel) &&
    Boolean(processingStatusText) &&
    Boolean(processingStopLabel) &&
    Boolean(onProcessingStop);

  const titleHeading = (
    <HeadingPage
      color="fg.default"
      w="full"
      minW={0}
      lineClamp={OBJECT_EDIT_TITLE_LINE_CLAMP}
      overflowWrap="anywhere"
    >
      {title}
    </HeadingPage>
  );

  return (
    <Box
      position="relative"
      h="full"
      w="full"
      display="flex"
      flexDirection="column"
      minH={0}
      minW={0}
      overflow="hidden"
      bg="bg.canvas"
      aria-busy={isProcessing ? true : undefined}
    >
      <Box
        flex={1}
        minH={0}
        minW={0}
        w="full"
        display="flex"
        justifyContent="stretch"
        px={5}
      >
        <VStack
          w="full"
          maxW={OBJECT_EDIT_CONTENT_MAX_WIDTH}
          minW={0}
          minH={0}
          h="full"
          alignItems="stretch"
          gap={0}
          px={3}
          position="relative"
          className="copilot-progressing-card"
          data-copilot-progressing={isProcessing ? 'true' : 'false'}
          data-copilot-working-layer-dismissed={
            isWorkingLayerDismissed ? 'true' : 'false'
          }
        >
          <VStack
            flexShrink={0}
            w="full"
            minW={0}
            alignItems="stretch"
            gap={0}
            pt={8}
            pointerEvents={isProcessing ? 'none' : undefined}
            {...(isProcessing ? { inert: true } : {})}
          >
            <VStack
              w="full"
              gap={3}
              alignItems="stretch"
              pb={3}
              borderBottomWidth="1px"
              borderColor="border.subtle"
            >
              <Box
                display="grid"
                w="full"
                minW={0}
                gridTemplateColumns="minmax(0, 1fr) auto"
                columnGap={3}
                rowGap={3}
                alignItems="start"
              >
                <VStack gap={1} alignItems="flex-start" minW={0} w="full">
                  <MetaDefault color="fg.muted">{eyebrow}</MetaDefault>
                  <HStack gap={2} alignItems="flex-start" minW={0} w="full">
                    {entityIcon ? (
                      <Box
                        flexShrink={0}
                        display="flex"
                        alignItems="center"
                        h={OBJECT_EDIT_TITLE_ICON_SLOT_HEIGHT}
                      >
                        {entityIcon}
                      </Box>
                    ) : null}
                    <Box flex={1} minW={0} w="full" display="block">
                      {typeof title === 'string' ? (
                        <Tooltip title={title}>
                          <Box minW={0} w="full" display="block">
                            {titleHeading}
                          </Box>
                        </Tooltip>
                      ) : (
                        titleHeading
                      )}
                    </Box>
                  </HStack>
                </VStack>
                <HStack
                  className="copilot-progressing-actions"
                  gap={1}
                  alignItems="center"
                  flexShrink={0}
                  flexWrap="wrap"
                  justifyContent="flex-end"
                  alignSelf="start"
                >
                  {actions}
                </HStack>
              </Box>
              {meta ? (
                <Box w="full" minW={0} overflow="hidden">
                  {meta}
                </Box>
              ) : null}
            </VStack>
          </VStack>

          <Box
            ref={scrollContainerRef}
            flex={1}
            minH={0}
            minW={0}
            w="full"
            overflow="auto"
            scrollbarGutter="stable"
            pt={3}
            pb={
              shouldReserveReviewScrollClearance
                ? `${OBJECT_EDIT_REVIEW_SCROLL_BOTTOM_CLEARANCE_PX}px`
                : 12
            }
            className="copilot-progressing-fields"
            data-review-scroll-clearance={
              shouldReserveReviewScrollClearance ? 'true' : undefined
            }
          >
            <Box
              minH={0}
              minW={0}
              w="full"
              pointerEvents={isProcessing ? 'none' : undefined}
              {...(isProcessing ? { inert: true } : {})}
            >
              <ObjectEditMeasureProvider>
                <ObjectEditMeasureColumn>{children}</ObjectEditMeasureColumn>
              </ObjectEditMeasureProvider>
            </Box>
          </Box>

          {showReviewBar ? suggestionReviewBar : null}

          {showProcessingChrome &&
          processingAriaLabel &&
          processingStatusText &&
          processingStopLabel &&
          onProcessingStop ? (
            <ObjectEditProcessingOverlay
              ariaLabel={processingAriaLabel}
              statusText={processingStatusText}
              stopLabel={processingStopLabel}
              onStop={onProcessingStop}
              showWorkingLayer={showWorkingLayer}
            />
          ) : null}
        </VStack>
      </Box>
    </Box>
  );
}
